/**
 * EnvironmentPicker — экран выбора рабочей среды после онбординга.
 *
 * Три варианта:
 *   1. Папка проекта (локально через Tauri-Desktop) — здесь disabled, ссылка на скачивание
 *   2. VPS (SSH-подключение) — переходит в форму подключения
 *   3. Веб-песочница (sandbox в Docker-volume) — самый быстрый старт
 *
 * Пользователь выбирает один раз, выбор сохраняется в localStorage.
 * Можно сменить через Settings → Подключение.
 */

import { useState } from "react";
import { motion } from "framer-motion";
import { FolderOpen, Server, Globe, Check, ArrowLeft, ExternalLink } from "lucide-react";
import { cn } from "../../lib/cn";
import { VpsPanel } from "../vps/VpsPanel";

export type EnvKind = "local" | "vps" | "sandbox";

const ENV_KEY = "aicores.environment";

export function getEnvironment(): EnvKind | null {
  if (typeof window === "undefined") return null;
  const v = localStorage.getItem(ENV_KEY);
  return v === "local" || v === "vps" || v === "sandbox" ? v : null;
}

export function setEnvironment(env: EnvKind): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(ENV_KEY, env);
}

interface Props {
  onSelect: (env: EnvKind) => void;
}

export function EnvironmentPicker({ onSelect }: Props) {
  const [selected, setSelected] = useState<EnvKind | null>(null);

  if (selected === "vps") {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center p-4 sm:p-8">
        <div className="w-full max-w-xl space-y-4">
          <button
            onClick={() => setSelected(null)}
            className="btn-ghost gap-1.5 text-fg-muted"
          >
            <ArrowLeft className="size-3.5" />
            Назад к выбору среды
          </button>
          <div className="card p-6 space-y-4">
            <h2 className="text-h1">Подключение к VPS</h2>
            <p className="text-small text-fg-muted">
              Введите данные SSH-доступа. Ключи и пароли хранятся локально в вашем браузере.
            </p>
            <VpsPanel
              autoShowForm
              onConnected={() => {
                setEnvironment("vps");
                onSelect("vps");
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center p-4 sm:p-8">
      <div className="w-full max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="text-center space-y-2 mb-10"
        >
          <h1 className="text-h1">Где будем работать?</h1>
          <p className="text-fg-muted">
            Выберите среду — где AICORES будет читать файлы и запускать команды.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <EnvCard
            icon={Globe}
            title="Веб-песочница"
            desc="Изолированный workspace на нашем сервере. Подходит для прототипов, обучения, быстрых проверок."
            badge="Быстрый старт"
            badgeColor="bg-green-500/15 text-green-400"
            onClick={() => {
              setEnvironment("sandbox");
              onSelect("sandbox");
            }}
          />
          <EnvCard
            icon={Server}
            title="VPS"
            desc="Подключение к вашему серверу по SSH. Агент работает с реальными файлами и сервисами."
            badge="Production"
            badgeColor="bg-accent-500/15 text-accent-400"
            onClick={() => setSelected("vps")}
          />
          <EnvCard
            icon={FolderOpen}
            title="Папка проекта"
            desc="Локальная папка на вашем компьютере. Только в Desktop-приложении."
            badge="Только Desktop"
            badgeColor="bg-bg-subtle text-fg-muted"
            disabled
            footer={
              <a
                href="https://app.aicores.ru/desktop/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-small text-accent-400 hover:underline"
              >
                Скачать Desktop
                <ExternalLink className="size-3" />
              </a>
            }
          />
        </div>

        <div className="mt-6 text-center text-small text-fg-muted">
          Можно сменить позже в Настройках → Подключение.
        </div>
      </div>
    </div>
  );
}

function EnvCard({
  icon: Icon,
  title,
  desc,
  badge,
  badgeColor,
  onClick,
  disabled,
  footer,
}: {
  icon: typeof FolderOpen;
  title: string;
  desc: string;
  badge: string;
  badgeColor: string;
  onClick?: () => void;
  disabled?: boolean;
  footer?: React.ReactNode;
}) {
  return (
    <motion.button
      whileHover={!disabled ? { y: -3 } : undefined}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "card p-6 text-left flex flex-col gap-3 transition-all",
        disabled
          ? "opacity-60 cursor-default"
          : "hover:border-accent-500/40 hover:shadow-accent cursor-pointer",
      )}
    >
      <div className="flex items-center justify-between">
        <div className="size-12 rounded-card bg-gradient-to-br from-accent-400/20 to-accent-700/20 grid place-items-center text-accent-400">
          <Icon className="size-6" />
        </div>
        <span className={cn("px-2 py-0.5 rounded text-[0.7rem] font-medium", badgeColor)}>
          {badge}
        </span>
      </div>
      <h3 className="text-h2">{title}</h3>
      <p className="text-small text-fg-muted leading-relaxed flex-1">{desc}</p>
      {footer && <div>{footer}</div>}
      {!disabled && (
        <div className="flex items-center gap-1 text-small text-accent-400 font-medium">
          Выбрать
          <Check className="size-3.5" />
        </div>
      )}
    </motion.button>
  );
}
